import ContentIllustration from '../../components/ui/ContentIllustration';
import SiteFooter from '../../components/layout/SiteFooter';
import { Link, useSearchParams } from 'react-router-dom';
import { useState } from 'react';
import { ArrowRight, Calendar, CheckCircle, ChevronDown, ChevronUp } from 'lucide-react';
import usePageTitle from '../../hooks/usePageTitle';
import useRevealCards from '../../hooks/useRevealCards';

/**
 * /aide-a-la-preparation-concours-atsem
 *
 * Landing publique SEO + acquisition pour le concours ATSEM
 * (externe, interne, 3ème concours). Long format pédagogique
 * avec programme officiel, méthodologie épreuve QCM + oral et CTA gratuit.
 *
 * Ton : tutoiement, chaleureux, encourageant.
 * CTA principal : "Créer mon compte gratuit".
 *
 * Slug du scope : `atsem`. Images : `atsem-externe.webp`, `atsem-interne.webp`,
 * `atsem-troisieme.webp` dans public/img/.
 */

const CONCOURS = [
    {
        "slug": "externe",
        "nom": "Concours externe ATSEM",
        "categorie": "Catégorie C",
        "niveau": "CAP AEPE, équivalence reconnue ou dispense",
        "bgImage": "/img/atsem-externe.webp",
        "stats": [
            {
                "value": "20 questions",
                "label": "QCM à l’écrit"
            },
            {
                "value": "45 min",
                "label": "écrit (coef. 1)"
            },
            {
                "value": "15 min",
                "label": "oral (coef. 2)"
            },
            {
                "value": "CDG",
                "label": "postes fixés à l’ouverture"
            }
        ],
        "details": [
            "Écrit : situations concrètes du métier, sous forme de QCM",
            "Oral : aptitude, motivation et environnement professionnel",
            "Réussite au concours : inscription sur liste d’aptitude, puis recherche d’un poste"
        ]
    },
    {
        "slug": "interne",
        "nom": "Concours interne ATSEM",
        "categorie": "Catégorie C",
        "niveau": "2 ans de services publics effectifs auprès de jeunes enfants en milieu scolaire et maternel",
        "bgImage": "/img/atsem-interne.webp",
        "stats": [
            {
                "value": "3 à 5",
                "label": "questions rédigées"
            },
            {
                "value": "2 h",
                "label": "écrit (coef. 1)"
            },
            {
                "value": "20 min",
                "label": "oral (coef. 2)"
            },
            {
                "value": "5 min max.",
                "label": "exposé du parcours"
            }
        ],
        "details": [
            "Écrit à partir d’un dossier succinct ; réponses courtes ou tableaux",
            "Entretien appuyé sur un document retraçant le parcours professionnel",
            "Ouvert aux agents publics remplissant les conditions, y compris les contractuels"
        ]
    },
    {
        "slug": "troisieme",
        "nom": "3e concours ATSEM",
        "categorie": "Catégorie C",
        "niveau": "4 ans d’activités professionnelles, de mandats locaux ou de responsabilités associatives, selon les conditions applicables",
        "bgImage": "/img/atsem-troisieme.webp",
        "stats": [
            {
                "value": "3 à 5",
                "label": "questions rédigées"
            },
            {
                "value": "2 h",
                "label": "écrit (coef. 1)"
            },
            {
                "value": "20 min",
                "label": "oral (coef. 2)"
            },
            {
                "value": "5 min max.",
                "label": "exposé du parcours"
            }
        ],
        "details": [
            "Les activités professionnelles sont recevables quelle que soit leur nature",
            "Écrit à partir d’un dossier succinct, puis entretien sur l’expérience",
            "Le CDG vérifie les justificatifs et les périodes prises en compte"
        ]
    }
];

const PROGRAMME_QCM = [
    { titre: 'Hygiène et sécurité de l\'enfant', detail: 'Soins, alimentation, sommeil, prévention des accidents, gestes de premiers secours, propreté' },
    { titre: 'Développement de l\'enfant 0-6 ans', detail: 'Psychomotricité, langage, autonomie, socialisation, repères de développement par âge' },
    { titre: 'Rôle et missions de l\'ATSEM', detail: 'Statut particulier des ATSEM (décret n° 92-850 modifié), assistance à l\'enseignant, posture professionnelle, communication parents-école' },
    { titre: 'École maternelle', detail: 'Programmes en vigueur de l’école maternelle, conseil d\'école, dispositifs d\'inclusion (PAI, PPS, PPRE), évaluation positive' },
    { titre: 'Entretien des locaux et matériel', detail: 'Nettoyage quotidien de la classe et des sanitaires, grand ménage, produits et pictogrammes de danger, protocole de dilution, plan de nettoyage et désinfection' },
    { titre: 'Sécurité en milieu scolaire', detail: 'PPMS, exercices d\'évacuation incendie, confinement et intrusion, registre de sécurité, travailler en sécurité (gestes et postures)' },
    { titre: 'Statut de la FPT', detail: 'Cadre d’emplois social de catégorie C, droits et devoirs des agents publics, hiérarchie communale, secret professionnel' },
    { titre: 'Collectivités territoriales et institutions', detail: 'Commune, département, région, intercommunalité, décentralisation, élections locales, institutions de la Ve République, laïcité' },
];

// Inventaire réel de la base ATSEM — chiffres RELEVÉS le 30/07/2026 sur les
// questions ONLINE, via la réponse brute de l'API :
//   curl -s "https://monquiz.fr/backend/api/scopes/detail.php?slug=atsem"
// puis somme des nb_questions par catégorie. Ne PAS estimer ces valeurs : une
// page publique qui gonfle ses chiffres coûte plus cher que pas de chiffres.
// À réactualiser (et redater ci-dessous) quand le contenu bouge.
const BASE_RELEVE = '30 juillet 2026';
const BASE_ATSEM = [
    { cat: 'Mathématiques', sujets: 7, questions: 509, note: 'proportions, pourcentages, conversions, problèmes de la vie courante' },
    { cat: 'Métier ATSEM', sujets: 13, questions: 240, note: 'missions, double hiérarchie, école maternelle, sécurité, entretien des locaux' },
    { cat: 'Droit de la fonction publique', sujets: 8, questions: 196, note: 'statut, droits et obligations, carrière, discipline' },
    { cat: 'Français', sujets: 5, questions: 194, note: 'orthographe, grammaire, vocabulaire, compréhension de texte' },
    { cat: 'Action sanitaire et sociale', sujets: 6, questions: 115, note: 'développement de l\'enfant, santé, handicap, petite enfance' },
    { cat: 'Inspiré des annales ATSEM', sujets: 5, questions: 100, note: 'séries au format de l\'épreuve, sur le modèle des sujets de CDG' },
    { cat: 'Droit public et institutions', sujets: 3, questions: 55, note: 'collectivités territoriales, Ve République, laïcité' },
];

// Ce qu'on ne fournit pas, et où le chercher. Section volontaire : un candidat
// qui découvre le manque après avoir payé ne revient pas.
const AILLEURS = [
    { manque: 'Un cours structuré', ou: 'MonQuiz t\'entraîne, il ne t\'enseigne pas. Si tu partes de zéro sur une notion, un manuel (Vuibert, Foucher, Studyrama) ou une prépa avec cours reste nécessaire.' },
    { manque: 'Les annales officielles corrigées', ou: 'Les sujets réels appartiennent aux Centres de Gestion. Va les chercher directement sur le site du CDG qui organise ton concours : c\'est gratuit et c\'est la référence du format.' },
    { manque: 'Un entraînement à l\'oral avec un jury', ou: 'Rien ne remplace une simulation face à quelqu\'un. Prépa locale, collègue ATSEM, ou association de candidats. Notre page te donne la méthode, pas l\'interlocuteur.' },
    { manque: 'Des cours en direct et des replays', ou: 'Ce n\'est pas notre métier. Certaines prépas en ligne proposent des masterclass ; elles se combinent très bien avec un banc d\'entraînement comme le nôtre.' },
];

const PROGRAMME_ORAL = [
    { titre: 'Présentation de motivation', detail: 'À l’interne et au troisième concours, un exposé de 5 minutes au plus présente ton expérience ; à l’externe, l’entretien porte sur tes aptitudes et ta motivation' },
    { titre: 'Connaissance du métier', detail: 'Missions ATSEM, statut FPT, articulation avec l\'enseignant, périscolaire, communication avec les parents' },
    { titre: 'Mises en situation', detail: 'Cas pratiques : enfant qui pleure, parent agressif, blessure mineure, geste d\'hygiène, conflit entre enfants' },
    { titre: 'Posture professionnelle', detail: 'Confidentialité, secret professionnel, neutralité, respect de la hiérarchie, gestion des émotions' },
];

const ETAPES_PREPARATION = [
    { mois: '6 mois avant', focus: 'Acquérir ou réactiver les notions du CAP AEPE. Lire l\'actualité de l\'école maternelle (programmes en vigueur, dispositifs récents).' },
    { mois: '3 mois avant', focus: 'Maîtriser le programme QCM par thème. Travailler les distracteurs typiques (faux amis sur hygiène, sécurité, statut).' },
    { mois: '1 mois avant', focus: 'Séries de QCM chronométrées (45 min pour 20 questions). Préparer 3 à 5 mises en situation type pour l\'oral.' },
    { mois: '1 semaine avant', focus: 'Relecture des fiches synthèse, sommeil réglé, repérage du centre d\'examen, tenue préparée.' },
];

const FAQ = [
    {
        "question": "Quel diplôme pour passer le concours ATSEM externe ?",
        "reponse": "Le CAP AEPE (ou ancien CAP petite enfance) est la voie habituelle. Une qualification reconnue équivalente peut être admise, sur décision de la commission compétente. Des dispenses de diplôme existent notamment pour les parents d’au moins trois enfants et les sportifs de haut niveau. Vérifie la notice du CDG : un autre diplôme ne vaut pas automatiquement équivalence."
    },
    {
        "question": "Combien de places au concours ATSEM externe ?",
        "reponse": "Le nombre de postes est fixé dans l’arrêté d’ouverture de chaque organisateur. Consulte les annonces et la répartition par voie sur le site du CDG. Il n’y a pas de total national annuel vérifié présenté ici."
    },
    {
        "question": "Quelle différence entre concours externe, interne et 3ème voie ATSEM ?",
        "reponse": "L’externe est ouvert avec le diplôme requis, une équivalence reconnue ou une dispense. L’interne demande deux ans de services publics effectifs auprès de jeunes enfants en milieu scolaire et maternel, au 1er janvier de l’année du concours, et ne se limite pas aux titulaires territoriaux. Le troisième concours demande quatre ans d’activités professionnelles, de mandats locaux ou de responsabilités associatives recevables ; l’expérience professionnelle peut être de toute nature. Les conditions détaillées figurent dans la fiche concours et la notice du CDG."
    },
    {
        "question": "Comment se déroule l’épreuve QCM ATSEM ?",
        "reponse": "À l’externe : 20 questions en 45 minutes, coefficient 1, sur des situations concrètes du métier. Plusieurs réponses exactes peuvent être attendues. Le barème peut pénaliser une réponse absente, fausse ou incomplète ; lis les règles sur le sujet. Le jury fixe le seuil d’admissibilité. Une note inférieure à 5 sur 20 est éliminatoire ; la moyenne minimale de 10 sur 20 concerne l’admission, coefficients appliqués."
    },
    {
        "question": "Comment se déroule l’oral d’admission ATSEM ?",
        "reponse": "À l’externe, l’entretien dure 15 minutes, coefficient 2, sur les aptitudes, la motivation et l’environnement professionnel. À l’interne et au troisième concours, il dure 20 minutes, coefficient 2, dont un exposé initial de 5 minutes au plus sur l’expérience, à partir du document de parcours remis à l’inscription. Des mises en situation peuvent être proposées."
    },
    {
        "question": "Quel salaire débutant pour un ATSEM ?",
        "reponse": "La rémunération dépend du grade, de l’échelon, du temps de travail et du régime indemnitaire de la collectivité. Compare les offres d’emploi et leurs conditions ; un montant net unique ne décrit pas toutes les situations."
    },
    {
        "question": "Quelle évolution de carrière après ATSEM ?",
        "reponse": "Le cadre d’emplois comprend les grades d’ATSEM principal de 2e classe et de 1re classe. L’avancement est soumis aux conditions statutaires. Une évolution vers un autre métier ou cadre d’emplois dépend des concours, formations et diplômes demandés : ce n’est pas un accès automatique."
    },
    {
        "question": "Comment s’entraîner à l’épreuve écrite de l’interne ou de la 3ème voie ATSEM ?",
        "reponse": "Les deux voies comportent 3 à 5 questions rédigées à partir d’un dossier succinct, en 2 heures, coefficient 1. À l’interne, les réponses peuvent aussi prendre la forme de tableaux. Entraîne-toi sur les sujets des CDG et fais relire tes réponses. MonQuiz propose des questions à réponse rédigée corrigées point par point, pour travailler la restitution des connaissances."
    },
    {
        "question": "Où trouver les annales corrigées du concours ATSEM ?",
        "reponse": "Consulte le site du CDG organisateur : il peut publier les sujets, des indications de correction et les rapports de jury. Ce sont les références pour comprendre le format. Les séries d’entraînement MonQuiz sont complémentaires de ces documents."
    },
    {
        "question": "Faut-il un cours ou des QCM pour préparer le concours ATSEM ?",
        "reponse": "Un cours permet de découvrir les notions ; les QCM servent à vérifier tes connaissances et à revoir les erreurs. Si tu débutes, complète l’entraînement par un manuel ou une préparation structurée. Pour l’interne et le troisième concours, prévois aussi un entraînement aux réponses rédigées."
    },
    {
        "question": "Quand passer son concours ATSEM ?",
        "reponse": "Consulte le calendrier et l’avis d’ouverture du CDG pour les dates de ta session. Lorsque le même concours est organisé simultanément par plusieurs centres de gestion, l’inscription se fait auprès d’un seul organisateur. La préinscription en ligne doit ensuite être validée selon les modalités de sa notice."
    }
];

export default function AidePreparationATSEM() {
    usePageTitle('Aide à la préparation du concours ATSEM — Externe, Interne, 3ème voie');
    useRevealCards(); // cartes .scope-card-landing révélées au défilement (sinon invisibles)
    const [searchParams] = useSearchParams();
    const fromParam = searchParams.get('from') || 'landing-atsem';
    const registerHref = `/inscription?from=${encodeURIComponent(fromParam)}`;
    const scopeHref = '/atsem';

    const [openFaq, setOpenFaq] = useState(null);

    return (
        <div className="livre-page aide-preparation-page aide-content-illustrated">
            <div className="livre-inner">

                {/* HERO */}
                <section className="livre-section livre-section--hero">
                    <div className="aide-freshness-badge">
                        <Calendar size={14} />
                        <span>Mis à jour pour la session {new Date().getFullYear() + 1}</span>
                    </div>
                    <h1 className="livre-title">
                        Préparer le concours <span className="livre-title-accent">ATSEM 2026-2027</span>
                    </h1>
                    <div className="livre-hero-text">
                        <p>
                            Tu veux devenir <strong>Agent Territorial Spécialisé des Écoles Maternelles</strong> ?
                            MonQuiz propose une aide à la préparation : des <strong>QCM dédiés ATSEM</strong> sur hygiène et sécurité, développement de l'enfant 0-6 ans, rôle ATSEM,
                            école maternelle, statut FPT et notions juridiques de base.
                        </p>
                        <p style={{ fontStyle: 'italic', color: 'var(--text-muted)' }}>
                            Ici on parle vrai : pas de promesse de réussite garantie, juste un outil qui te fait gagner du temps
                            sur l'entraînement et le suivi de tes faiblesses.
                        </p>
                    </div>
                    <div className="aide-hero-actions">
                        <Link to={registerHref} className="btn btn-primary decouvrir-cta-btn aide-hero-cta">
                            Créer mon compte gratuit <ArrowRight size={16} />
                        </Link>
                        <Link
                            to={scopeHref}
                            className="scope-card-landing aide-hero-scope-card"
                            style={{
                                backgroundImage: `linear-gradient(to bottom, rgba(0,0,0,0.45) 0%, rgba(0,0,0,0.75) 100%), url('/img/atsem-externe.webp')`,
                                backgroundSize: 'cover',
                                backgroundPosition: 'center',
                                color: '#fff',
                            }}
                        >
                            <div className="scope-card-icon content-mode-icon" style={{ background: 'rgba(255,255,255,0.2)', color: '#fff' }}>
                                <ContentIllustration name="atsem" size={56} />
                            </div>
                            <h3 style={{ color: '#fff' }}>Concours ATSEM</h3>
                            <p style={{ color: 'rgba(255,255,255,0.85)' }}>
                                QCM dédiés à la préparation ATSEM
                            </p>
                            <span className="scope-card-link" style={{ color: 'rgba(255,255,255,0.95)' }}>
                                Découvrir les quiz <ArrowRight size={14} strokeWidth={2} style={{ display: 'inline', verticalAlign: 'middle' }} />
                            </span>
                        </Link>
                    </div>
                </section>

                {/* LIEN VERS LA PAGE CALENDRIER (SEO — maillage descendant) */}
                <section className="livre-section">
                    <a href="/dates-concours-atsem-2027" style={{
                        display: 'flex', alignItems: 'center', gap: '0.9rem', flexWrap: 'wrap',
                        padding: '1rem 1.25rem', borderRadius: '14px',
                        border: '1px solid var(--card-border)', background: 'var(--card-bg)',
                        textDecoration: 'none', color: 'var(--text)',
                    }}>
                        <span style={{ fontSize: '1.5rem', lineHeight: 1 }} aria-hidden="true">📅</span>
                        <span style={{ flex: 1, minWidth: '220px' }}>
                            <strong>Calendrier du concours ATSEM 2027</strong>
                            <span style={{ display: 'block', color: 'var(--text-muted)', fontSize: '0.9rem' }}>
                                Dates d'inscription, écrit, résultats — et alerte email à l'ouverture.
                            </span>
                        </span>
                        <span style={{ color: 'var(--primary)', fontWeight: 700, whiteSpace: 'nowrap' }}>Voir les dates →</span>
                    </a>
                </section>

                {/* COMPARATIF EXTERNE / INTERNE / 3ème VOIE */}
                <section className="livre-section">
                    <h2 className="livre-section-title">Les 3 voies d'accès au concours ATSEM</h2>
                    <p><a href="/concours-atsem">Consulter la fiche concours : conditions, épreuves et sources officielles</a></p>
                    <p className="livre-section-sub">
                        Le concours ATSEM s'organise via les Centres de Gestion (CDG) départementaux. Selon ton profil
                        (diplôme, statut actuel, expérience), tu pourras passer le concours externe, interne ou la 3ème voie.
                    </p>
                    <div className="aide-concours-grid">
                        {CONCOURS.map((c) => {
                            return (
                                <article
                                    key={c.slug}
                                    className={`aide-concours-card aide-concours-card--${c.slug}`}
                                    style={{ backgroundImage: `url('${c.bgImage}')` }}
                                >
                                    <div className="aide-concours-card-content">
                                        <span className="aide-concours-card-cat">
                                            {c.categorie} — {c.niveau}
                                        </span>
                                        <h3>{c.nom}</h3>
                                        <div className="aide-concours-stats">
                                            {c.stats.map((s, j) => (
                                                <div key={j} className="aide-concours-stat">
                                                    <strong>{s.value}</strong>
                                                    <span>{s.label}</span>
                                                </div>
                                            ))}
                                        </div>
                                        <ul className="aide-concours-details">
                                            {c.details.map((d, j) => <li key={j}>{d}</li>)}
                                        </ul>
                                    </div>
                                </article>
                            );
                        })}
                    </div>
                </section>

                {/* PROGRAMME OFFICIEL */}
                <section className="livre-section">
                    <h2 className="livre-section-title">Les grands thèmes de l'épreuve QCM ATSEM</h2>
                    <p className="livre-section-sub">
                        Le QCM d'admissibilité du concours externe ATSEM porte sur des situations professionnelles. Les centres de gestion
                        publient une note de cadrage qui liste les domaines ; elle varie selon le CDG. MonQuiz propose au moins un sujet
                        pour chacun, sauf deux points traités en peu de questions (gestes et postures, droit à l'image).
                        Selon le CDG, l'épreuve peut aussi comporter du français et du calcul appliqué : c'est couvert aussi.
                    </p>

                    <div className="livre-section-block">
                        <h3 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <ContentIllustration name="book-open" size={32} /> Épreuve QCM (admissibilité)
                        </h3>
                        <ul style={{ paddingLeft: '1.5rem' }}>
                            {PROGRAMME_QCM.map((p, i) => (
                                <li key={i} style={{ marginBottom: '0.5rem' }}>
                                    <strong>{p.titre}</strong> — {p.detail}
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="livre-section-block" style={{ marginTop: '1.5rem' }}>
                        <h3 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <ContentIllustration name="book-open" size={32} /> Épreuve orale (admission)
                        </h3>
                        <ul style={{ paddingLeft: '1.5rem' }}>
                            {PROGRAMME_ORAL.map((p, i) => (
                                <li key={i} style={{ marginBottom: '0.5rem' }}>
                                    <strong>{p.titre}</strong> — {p.detail}
                                </li>
                            ))}
                        </ul>
                    </div>
                </section>

                {/* STRATÉGIE DE PRÉPARATION */}
                <section className="livre-section">
                    <h2 className="livre-section-title">Stratégie de préparation au concours ATSEM en 4 étapes</h2>
                    <p className="livre-section-sub">
                        Plus tu commences tôt, plus tu peux étaler ta préparation et choisir le CDG organisateur.
                        Voici un calendrier indicatif basé sur les retours des candidats reçus.
                    </p>
                    <div className="decouvrir-modes">
                        {ETAPES_PREPARATION.map((e, i) => (
                            <div key={i} className="decouvrir-mode">
                                <div className="decouvrir-mode-icon content-mode-icon">
                                    {i === 0 && <ContentIllustration name="calendar" size={56} />}
                                    {i === 1 && <ContentIllustration name="target" size={56} />}
                                    {i === 2 && <ContentIllustration name="file-text" size={56} />}
                                    {i === 3 && <ContentIllustration name="clock" size={56} />}
                                </div>
                                <div className="decouvrir-mode-body">
                                    <strong>{e.mois}</strong>
                                    <p>{e.focus}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>

                {/* THÈMES CLÉS DU QCM */}
                <section className="livre-section">
                    <h2 className="livre-section-title">Thèmes récurrents du QCM ATSEM</h2>
                    <p className="livre-section-sub">
                        D'après les sessions récentes, certains thèmes reviennent quasi systématiquement.
                        Voici les zones à maîtriser en priorité, et celles où les candidats chutent le plus.
                    </p>

                    <div className="decouvrir-honest">
                        <div className="decouvrir-honest-col decouvrir-honest-col--good">
                            <h3><ContentIllustration name="award" size={32} /> Thèmes incontournables</h3>
                            <ul>
                                <li><strong>Hygiène</strong> — change, propreté, alimentation, soins courants, premiers secours</li>
                                <li><strong>Sécurité</strong> — prévention des accidents, fiches sanitaires, protocoles incendie</li>
                                <li><strong>Développement enfant 0-6 ans</strong> — repères psychomoteurs, langage, autonomie</li>
                                <li><strong>Rôle ATSEM</strong> — décret 2018-152, articulation avec l'enseignant, posture</li>
                            </ul>
                        </div>
                        <div className="decouvrir-honest-col decouvrir-honest-col--good">
                            <h3><ContentIllustration name="award" size={32} /> Zones de pièges classiques</h3>
                            <ul>
                                <li><strong>Statut FPT</strong> — confusion catégorie B/C, filière médico-sociale vs animation</li>
                                <li><strong>Communication parents</strong> — limites de l'ATSEM (pas de diagnostic, pas de jugement)</li>
                                <li><strong>Conseil d'école</strong> — composition, fréquence, place de l'ATSEM (non membre de droit)</li>
                                <li><strong>Inclusion / handicap</strong> — différencier PAI, PPS, PPRE, PPC (faux amis fréquents)</li>
                            </ul>
                        </div>
                    </div>
                </section>

                {/* MÉTHODOLOGIE */}
                <section className="livre-section">
                    <h2 className="livre-section-title">Méthodologie des épreuves ATSEM</h2>
                    <p className="livre-section-sub">
                        Au-delà du contenu, la méthode fait la différence. Voici les bons réflexes
                        à adopter pour le QCM comme pour l'oral.
                    </p>
                    <div className="decouvrir-modes">
                        <div className="decouvrir-mode">
                            <div className="decouvrir-mode-icon content-mode-icon"><ContentIllustration name="file-text" size={56} /></div>
                            <div className="decouvrir-mode-body">
                                <strong>Méthode QCM ATSEM</strong>
                                <p>20 questions en 45 minutes (concours externe) : ~2 min par question. Première lecture rapide pour repérer les questions sûres, deuxième passe sur les hésitations, troisième pour les distracteurs. Si pénalité : ne pas répondre au hasard.</p>
                            </div>
                        </div>
                        <div className="decouvrir-mode">
                            <div className="decouvrir-mode-icon content-mode-icon"><ContentIllustration name="file-text" size={56} /></div>
                            <div className="decouvrir-mode-body">
                                <strong>Méthode présentation orale (3-5 min)</strong>
                                <p>Structure : parcours personnel → expériences avec enfants → motivation ATSEM → projet professionnel. Éviter les généralités, donner des exemples concrets vécus. Travailler le timing à voix haute, en se chronométrant.</p>
                            </div>
                        </div>
                        <div className="decouvrir-mode">
                            <div className="decouvrir-mode-icon content-mode-icon"><ContentIllustration name="file-text" size={56} /></div>
                            <div className="decouvrir-mode-body">
                                <strong>Méthode mises en situation</strong>
                                <p>Schéma type : 1/ je rassure l'enfant ou j'observe la situation, 2/ j'évalue le contexte, 3/ je réagis dans le respect de mon rôle (pas de diagnostic médical, pas de jugement), 4/ je transmets à l'enseignant ou à la hiérarchie si nécessaire.</p>
                            </div>
                        </div>
                        <div className="decouvrir-mode">
                            <div className="decouvrir-mode-icon content-mode-icon"><ContentIllustration name="file-text" size={56} /></div>
                            <div className="decouvrir-mode-body">
                                <strong>Posture professionnelle</strong>
                                <p>3 principes : confidentialité (secret professionnel sur les enfants et les familles), neutralité (laïcité, pas de prise de position politique ou religieuse), loyauté (respect de l'enseignant, de la directrice, du maire). À mentionner spontanément en oral.</p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* QRC — RÉPONSES RÉDIGÉES (interne / 3ème voie) */}
                <section className="livre-section">
                    <h2 className="livre-section-title">Nouveau : t'entraîner aux questions à réponse rédigée (QRC)</h2>
                    <p className="livre-section-sub">
                        Sur les voies interne et 3ème concours, l'admissibilité ne se joue pas sur des cases à cocher :
                        il faut <strong>rédiger</strong>. C'est là que la plupart des candidats s'entraînent le moins,
                        faute d'un correcteur pour lire leurs réponses.
                    </p>

                    <div className="livre-section-block">
                        <p>
                            On a ouvert une série de <strong>questions à réponse rédigée sur le métier d'ATSEM et
                            l'action sanitaire et sociale</strong>. Tu rédiges ta réponse avec tes mots, puis elle est
                            corrigée point par point face aux attendus du barème.
                        </p>
                        <ul style={{ paddingLeft: '1.5rem' }}>
                            <li style={{ marginBottom: '0.4rem' }}><strong>Un verdict par attendu</strong> — bien traité, partiellement, manquant, ou contredit</li>
                            <li style={{ marginBottom: '0.4rem' }}><strong>Un score et un seuil de réussite</strong> affichés clairement, pas une appréciation vague</li>
                            <li style={{ marginBottom: '0.4rem' }}><strong>Les erreurs bloquantes signalées</strong> — celles qui coûtent cher devant un jury, comme sortir de son rôle ou poser un diagnostic médical</li>
                            <li style={{ marginBottom: '0.4rem' }}><strong>Un exemple déjà corrigé, offert</strong> sur certains sujets, pour voir comment le correcteur évalue avant de t'abonner</li>
                        </ul>
                        <p style={{ fontStyle: 'italic', color: 'var(--text-muted)', marginTop: '0.8rem' }}>
                            À dire honnêtement : cet entraînement porte sur le <em>format</em> et sur le fond de tes réponses.
                            Il ne remplace pas les sujets réels de ton CDG, qui restent la référence, et un correcteur
                            automatique n'a pas le dernier mot d'un jury.
                        </p>
                    </div>
                </section>

                {/* INVENTAIRE RÉEL DE LA BASE */}
                <section className="livre-section">
                    <h2 className="livre-section-title">Ce que contient vraiment notre base ATSEM</h2>
                    <p className="livre-section-sub">
                        Plutôt que « des centaines de questions », voici le compte exact, catégorie par catégorie.
                        Soit <strong>{BASE_ATSEM.reduce((s, b) => s + b.questions, 0).toLocaleString('fr-FR')} questions</strong> réparties
                        sur <strong>{BASE_ATSEM.reduce((s, b) => s + b.sujets, 0)} sujets</strong>, chacune avec son explication rédigée.
                        Chiffres relevés le {BASE_RELEVE}.
                    </p>
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.92rem' }}>
                            <thead>
                                <tr style={{ textAlign: 'left', color: 'var(--text-muted)', fontSize: '0.82rem', textTransform: 'uppercase', letterSpacing: '.04em' }}>
                                    <th style={{ padding: '8px 10px' }}>Catégorie</th>
                                    <th style={{ padding: '8px 10px', textAlign: 'right' }}>Sujets</th>
                                    <th style={{ padding: '8px 10px', textAlign: 'right' }}>Questions</th>
                                    <th style={{ padding: '8px 10px' }}>Contenu</th>
                                </tr>
                            </thead>
                            <tbody>
                                {BASE_ATSEM.map((b, i) => (
                                    <tr key={i} style={{ borderTop: '1px solid var(--card-border)' }}>
                                        <td style={{ padding: '8px 10px', fontWeight: 600 }}>{b.cat}</td>
                                        <td style={{ padding: '8px 10px', textAlign: 'right' }}>{b.sujets}</td>
                                        <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 600 }}>{b.questions}</td>
                                        <td style={{ padding: '8px 10px', color: 'var(--text-muted)' }}>{b.note}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </section>

                {/* CE QUE MONQUIZ APPORTE */}
                <section className="livre-section">
                    <h2 className="livre-section-title">Comment MonQuiz t'aide à préparer le concours ATSEM</h2>
                    <div className="decouvrir-honest">
                        <div className="decouvrir-honest-col decouvrir-honest-col--good">
                            <h3><CheckCircle size={16} /> Ce que tu trouves chez nous</h3>
                            <ul>
                                <li><strong>{BASE_ATSEM.reduce((s, b) => s + b.questions, 0).toLocaleString('fr-FR')} QCM dédiés ATSEM</strong> sur les thèmes de l'épreuve, plus des séries de français et de calcul pour la remise à niveau</li>
                                <li><strong>Des questions à réponse rédigée corrigées</strong> pour l'interne et la 3ème voie</li>
                                <li>QCM piège débusqués par 3 IA croisées (Haiku, Sonnet, Opus) + relecture humaine</li>
                                <li>Explication rédigée avec source (décret 2018-152, Code de l'éducation, CGFP) pour chaque question</li>
                                <li>5 modes d'entraînement : quiz par sujet, quiz mélangé, quiz intelligent, quiz sur erreurs, révisions du jour</li>
                                <li>Suivi de tes faiblesses par thème et par tag, automatique</li>
                                <li>Plusieurs séries de QCM gratuites pour tester avant de s'abonner</li>
                            </ul>
                        </div>
                        <div className="decouvrir-honest-col decouvrir-honest-col--good">
                            <h3><ContentIllustration name="trending-up" size={32} /> Ce qui te fait progresser</h3>
                            <ul>
                                <li>Tu travailles uniquement les questions où tu te trompes</li>
                                <li>La répétition espacée garde les notions du CAP AEPE vivantes</li>
                                <li>Tu identifies tes 5 faiblesses prioritaires en un coup d'œil</li>
                                <li>Les QCM méthodologiques te font intégrer les règles statut sans tout relire</li>
                                <li>Tu retrouves les thèmes communs aux sessions des CDG dans la même base</li>
                            </ul>
                        </div>
                    </div>

                    {/* Limites assumées : dire ce qu'on ne fait pas, et vers quoi orienter. */}
                    <div className="decouvrir-honest" style={{ marginTop: '1.5rem' }}>
                        <div className="decouvrir-honest-col decouvrir-honest-col--bad" style={{ gridColumn: '1 / -1' }}>
                            <h3><ContentIllustration name="file-text" size={32} /> Ce que tu ne trouveras pas ici, et où le chercher</h3>
                            <ul>
                                {AILLEURS.map((a, i) => (
                                    <li key={i}><strong>{a.manque}</strong> — {a.ou}</li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </section>

                {/* FAQ */}
                <section className="livre-section">
                    <h2 className="livre-section-title">Questions fréquentes sur le concours ATSEM</h2>
                    <div className="aide-faq">
                        {FAQ.map((item, i) => (
                            <div key={i} className="aide-faq-item">
                                <button
                                    type="button"
                                    className="aide-faq-question"
                                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                                    aria-expanded={openFaq === i}
                                >
                                    <span>{item.question}</span>
                                    {openFaq === i ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                                </button>
                                {openFaq === i && (
                                    <div className="aide-faq-answer">{item.reponse}</div>
                                )}
                            </div>
                        ))}
                    </div>

                    {/* JSON-LD pour SEO et IA (FAQPage + Organization + Course + BreadcrumbList) */}
                    <script
                        type="application/ld+json"
                        dangerouslySetInnerHTML={{
                            __html: JSON.stringify({
                                '@context': 'https://schema.org',
                                '@graph': [
                                    {
                                        '@type': 'FAQPage',
                                        mainEntity: FAQ.map(f => ({
                                            '@type': 'Question',
                                            name: f.question,
                                            acceptedAnswer: { '@type': 'Answer', text: f.reponse },
                                        })),
                                    },
                                    {
                                        '@type': 'Organization',
                                        name: 'MonQuiz.fr',
                                        url: 'https://monquiz.fr',
                                        logo: 'https://monquiz.fr/img/og-monquiz.webp',
                                        sameAs: ['https://monquiz.fr'],
                                        description: 'Plateforme française d\'entraînement aux concours par QCM avec QCM validés par 3 IA croisées et relecture humaine.',
                                        contactPoint: {
                                            '@type': 'ContactPoint',
                                            email: 'contact@monquiz.fr',
                                            contactType: 'customer support',
                                            areaServed: 'FR',
                                            availableLanguage: 'French',
                                        },
                                    },
                                    {
                                        '@type': 'Course',
                                        name: 'Préparation au concours ATSEM (Agent Territorial Spécialisé des Écoles Maternelles)',
                                        description: 'Thèmes de l\'épreuve, repères de méthode QCM et oral, QCM dédiés et questions à réponse rédigée corrigées pour préparer le concours ATSEM (catégorie C, filière médico-sociale, Fonction Publique Territoriale).',
                                        provider: {
                                            '@type': 'Organization',
                                            name: 'MonQuiz.fr',
                                            sameAs: 'https://monquiz.fr',
                                        },
                                        educationalLevel: 'Catégorie C (CAP AEPE requis pour le concours externe)',
                                        teaches: PROGRAMME_QCM.map(p => p.titre).join(', '),
                                        inLanguage: 'fr',
                                        hasCourseInstance: {
                                            '@type': 'CourseInstance',
                                            courseMode: 'online',
                                            url: 'https://monquiz.fr/atsem',
                                        },
                                    },
                                    {
                                        '@type': 'BreadcrumbList',
                                        itemListElement: [
                                            { '@type': 'ListItem', position: 1, name: 'Accueil', item: 'https://monquiz.fr' },
                                            { '@type': 'ListItem', position: 2, name: 'Concours ATSEM', item: 'https://monquiz.fr/atsem' },
                                            { '@type': 'ListItem', position: 3, name: 'Préparation concours ATSEM', item: 'https://monquiz.fr/aide-a-la-preparation-concours-atsem' },
                                        ],
                                    },
                                ],
                            }),
                        }}
                    />
                </section>

                {/* CTA FINAL */}
                <section className="livre-section livre-section--cta">
                    <h2 className="livre-section-title">Lance ta préparation au concours ATSEM</h2>
                    <p className="livre-section-sub">
                        L'inscription est gratuite. Tu accèdes immédiatement à plusieurs séries de QCM gratuites sur le scope ATSEM,
                        sans carte bancaire. Tu décides ensuite si tu passes en accès complet pour t'entraîner sur l'ensemble
                        des QCM dédiés ATSEM pour préparer les épreuves écrites.
                    </p>
                    <div className="decouvrir-cta-actions">
                        <Link to={registerHref} className="btn btn-primary decouvrir-cta-btn">
                            Créer mon compte gratuit <ArrowRight size={16} />
                        </Link>
                        <Link to="/connexion" className="btn btn-secondary">
                            J'ai déjà un compte
                        </Link>
                    </div>
                    {/* Cadrage : ce que l'abonnement est et n'est pas. Protège autant que ça rassure. */}
                    <p style={{ marginTop: '1.5rem', fontSize: '0.86rem', color: 'var(--text-muted)', maxWidth: '760px' }}>
                        MonQuiz est un outil d'entraînement à la préparation du concours ATSEM. Il ne vaut ni inscription
                        au concours, ni diplôme, ni certification, ni garantie de réussite. L'inscription au concours se fait
                        auprès du Centre de Gestion compétent, et il t'appartient de vérifier que tu remplis les conditions
                        d'accès à la voie que tu choisis.
                    </p>
                </section>

            </div>

            <SiteFooter />
        </div>
    );
}
